import { Component } from '@theme/component';

/** A native modal with a real, store-wide discount deadline. */
class KnFlashOffer extends Component {
  #controller;
  #delay;
  #expiry;
  #focus;
  #overflow;
  #scrollRoot;
  #rootOverflow;
  #key;
  #countdown;
  #windowEnd;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    const dialog = this.querySelector('dialog');
    if (!dialog) return;
    this.#key = `kn-offer-dismissed:${this.dataset.code}:${this.dataset.end}`;
    document.addEventListener('click', this.#handleTrigger, options);
    this.querySelectorAll('[data-offer-close]').forEach((button) => button.addEventListener('click', () => dialog.close(), options));
    this.querySelector('[data-offer-copy]')?.addEventListener('click', this.#copy, options);
    this.querySelector('[data-offer-apply]')?.addEventListener('click', this.#apply, options);
    dialog.addEventListener('close', this.#closed, options);
    dialog.addEventListener('click', (event) => {
      const rect = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) dialog.close();
    }, options);
    const formResult = this.querySelector('[data-offer-form-result]');
    if (formResult && location.hash === '#KnFlashCustomerForm') {
      formResult.closest('details')?.setAttribute('open', '');
      this.open();
    } else if (this.dataset.autoOpen !== 'false' && !window.Shopify?.designMode && !this.#dismissed()) {
      this.#delay = setTimeout(() => this.open(false), Number(this.dataset.delay) * 1000);
    }
    this.#scheduleExpiry();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    clearTimeout(this.#delay);
    clearTimeout(this.#expiry);
    clearInterval(this.#countdown);
    this.querySelector('dialog[open]')?.close();
    this.#restoreScroll();
    super.disconnectedCallback();
  }

  #active() {
    return Date.parse(this.dataset.end) > Date.now();
  }

  #scheduleExpiry() {
    clearTimeout(this.#expiry);
    const remaining = Date.parse(this.dataset.end) - Date.now();
    if (remaining <= 0) {
      this.querySelector('dialog[open]')?.close();
      document.dispatchEvent(new CustomEvent('kn:offer-expired'));
      return;
    }
    this.#expiry = setTimeout(() => this.#scheduleExpiry(), Math.min(remaining, 86400000));
  }

  #handleTrigger = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-kn-flash-open]') : null;
    if (!trigger || !this.#active()) return;
    event.preventDefault();
    this.open();
  };

  open(manual = true) {
    const dialog = this.querySelector('dialog');
    if (!dialog || dialog.open || !this.#active()) return;
    if (!manual && (document.hidden || document.querySelector('dialog[open]') || this.#dismissed())) return;
    clearTimeout(this.#delay);
    this.#focus = document.activeElement;
    this.#overflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.#scrollRoot = document.querySelector('.page-wrapper');
    if (this.#scrollRoot instanceof HTMLElement) {
      this.#rootOverflow = this.#scrollRoot.style.overflow;
      this.#scrollRoot.style.overflow = 'hidden';
    }
    dialog.showModal();
    this.#startCountdown();
    dialog.querySelector('[data-offer-close]')?.focus({ preventScroll: true });
  }

  // This is a persistent shopping-window display; Shopify owns discount validity.
  #startCountdown() {
    clearInterval(this.#countdown);
    const storageKey = `kn-offer-window:${this.dataset.code}:${this.dataset.end}`;
    if (!this.#windowEnd) {
      let saved = 0;
      try { saved = Number(sessionStorage.getItem(storageKey)); } catch { /* Storage is optional. */ }
      this.#windowEnd = saved > 0 ? saved : Date.now() + 30 * 60 * 1000;
      try { sessionStorage.setItem(storageKey, String(this.#windowEnd)); } catch { /* Storage is optional. */ }
    }
    const deadline = Math.min(this.#windowEnd, Date.parse(this.dataset.end));
    const update = () => {
      const seconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      const output = this.querySelector('[data-offer-countdown]');
      if (output) output.textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
      this.querySelector('[data-offer-timer]')?.removeAttribute('hidden');
      if (!seconds) clearInterval(this.#countdown);
    };
    update();
    if (deadline > Date.now()) this.#countdown = setInterval(update, 1000);
  }

  #dismissed() {
    try {
      return Date.now() - Number(localStorage.getItem(this.#key) || 0) < 3 * 86400000;
    } catch {
      return false;
    }
  }

  #dismiss() {
    try { localStorage.setItem(this.#key, String(Date.now())); } catch { /* Storage is optional. */ }
  }

  /** Preserve the current variant and campaign parameters through Shopify's discount redirect. @param {MouseEvent} event */
  #apply = (event) => {
    const link = event.currentTarget;
    if (link instanceof HTMLAnchorElement) {
      const url = new URL(link.href, location.origin);
      url.searchParams.set('redirect', `${location.pathname}${location.search}${location.hash}`);
      link.href = url.toString();
    }
    this.#dismiss();
  };

  #restoreScroll() {
    if (this.#overflow !== undefined) document.documentElement.style.overflow = this.#overflow;
    this.#overflow = undefined;
    if (this.#scrollRoot instanceof HTMLElement && this.#rootOverflow !== undefined) this.#scrollRoot.style.overflow = this.#rootOverflow;
    this.#rootOverflow = undefined;
    this.#scrollRoot = undefined;
  }

  #closed = () => {
    clearInterval(this.#countdown);
    this.#dismiss();
    this.#restoreScroll();
    if (this.#focus instanceof HTMLElement && this.#focus.isConnected) this.#focus.focus({ preventScroll: true });
  };

  #copy = async () => {
    const code = this.dataset.code || '';
    const status = this.querySelector('[data-offer-status]');
    try {
      await navigator.clipboard.writeText(code);
      if (status) status.textContent = this.dataset.copied;
    } catch {
      const element = this.querySelector('[data-offer-code]');
      if (element) {
        const range = document.createRange();
        range.selectNodeContents(element);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
      if (status) status.textContent = this.dataset.copyFallback;
    }
  };
}

if (!customElements.get('kn-flash-offer')) customElements.define('kn-flash-offer', KnFlashOffer);
